import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, API, BACKEND_URL, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Mic, Square, Send, Info, CheckCircle2, RotateCcw, Sparkles, Loader2, Volume2, VolumeX, Ear } from "lucide-react";
import { toast } from "sonner";

function useRecorder() {
  const recRef = useRef(null); const chunksRef = useRef([]); const startedAt = useRef(0);
  const [recording, setRecording] = useState(false);
  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
    const rec = new MediaRecorder(stream, { mimeType: mime });
    chunksRef.current = []; rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.start(250); recRef.current = rec; startedAt.current = Date.now(); setRecording(true);
  };
  const stop = () => new Promise((resolve) => {
    const rec = recRef.current; if (!rec) return resolve(null);
    rec.onstop = () => {
      rec.stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      setRecording(false); recRef.current = null;
      resolve({ blob, seconds: Math.max(1, Math.round((Date.now() - startedAt.current) / 1000)) });
    };
    rec.stop();
  });
  return { recording, start, stop };
}

async function transcribeBlob({ blob, seconds, scenarioId }) {
  const fd = new FormData();
  fd.append("audio", blob, "speech.webm"); fd.append("duration_seconds", String(seconds)); fd.append("scenario_id", scenarioId);
  const { data } = await api.post("/speech/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

function useSpeaker(scenarioId) {
  const audioRef = useRef(null);
  const [speakingText, setSpeakingText] = useState(null);
  const speak = async (text) => {
    if (!text) return;
    try {
      audioRef.current?.pause();
      const { data } = await api.post("/speech/tts", { text, scenario_id: scenarioId });
      const audio = new Audio(`${BACKEND_URL}${data.url}`);
      audioRef.current = audio; setSpeakingText(text);
      audio.onended = () => setSpeakingText(null); audio.onerror = () => setSpeakingText(null);
      await audio.play();
    } catch (err) { setSpeakingText(null); toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't play audio"); }
  };
  const stop = () => { audioRef.current?.pause(); setSpeakingText(null); };
  useEffect(() => () => audioRef.current?.pause(), []);
  return { speak, stop, speakingText };
}

function fmtMin(sec) { return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`; }

export function MissionsList() {
  const [ms, setMs] = useState([]);
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    api.get("/scenarios").then(({data}) => setMs(data.scenarios));
    api.get("/missions/usage").then(({data}) => setUsage(data.usage));
  }, []);
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display font-extrabold text-3xl text-slate-900">Real-Life Missions</h1>
          <p className="text-slate-600 mt-1">Practise conversations you actually face — with an AI partner who stays in character.</p></div>
        {usage && <UsageMeter usage={usage}/>}
      </header>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ms.map((s) => (
          <Link key={s.id} to={`/missions/${s.id}`} className="rle-card p-5 block" data-testid={`mission-card-${s.id}`}>
            <div className="flex items-center justify-between">
              <span className="rle-eyebrow">{s.goal} · {s.difficulty}</span>
              {s.premium && <Lock size={14} className="text-amber-600"/>}
            </div>
            <h3 className="mt-2 font-display font-semibold text-slate-900">{s.title}</h3>
            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{s.context}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

function UsageMeter({ usage }) {
  if (usage.unlimited) return (
    <div className="rle-card px-4 py-3 min-w-[220px]" data-testid="ai-usage-meter">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="flex items-center gap-1"><Sparkles size={12}/>AI turns this month</span>
        <span className="text-emerald-700 font-semibold" data-testid="ai-usage-text">{usage.used} · Unlimited (admin)</span>
      </div>
    </div>
  );
  const pct = usage.limit ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 100;
  const low = usage.remaining <= Math.max(1, Math.floor(usage.limit * 0.2));
  return (
    <div className="rle-card px-4 py-3 min-w-[220px]" data-testid="ai-usage-meter">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="flex items-center gap-1"><Sparkles size={12}/>AI turns this month</span>
        <span className={low ? "text-amber-700 font-semibold" : "text-slate-700 font-semibold"} data-testid="ai-usage-text">{usage.used} / {usage.limit}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] duration-500 ${low ? "bg-amber-500" : "bg-emerald-600"}`} style={{ width: `${pct}%` }}/>
      </div>
      {usage.remaining === 0 && <Link to="/pricing" className="text-xs text-emerald-700 underline mt-1 inline-block" data-testid="ai-usage-upgrade-link">Upgrade for more turns</Link>}
    </div>
  );
}

async function streamMissionReply({ scenarioId, message, onDelta, onDone }) {
  const res = await fetch(`${API}/missions/message`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scenario_id: scenarioId, message }),
  });
  if (!res.ok) {
    let detail = "Something went wrong.";
    try { detail = formatApiErrorDetail((await res.json()).detail); } catch {}
    throw new Error(detail);
  }
  const reader = res.body.getReader(); const decoder = new TextDecoder(); let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n"); buf = parts.pop();
    for (const part of parts) {
      const line = part.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      const ev = JSON.parse(line.slice(6));
      if (ev.type === "delta") onDelta(ev.content);
      else if (ev.type === "done") onDone(ev);
      else if (ev.type === "error") throw new Error(ev.detail);
    }
  }
}

export function MissionDetail() {
  const { scenarioId } = useParams(); const nav = useNavigate();
  const [sc, setSc] = useState(null); const [convo, setConvo] = useState(null);
  const [usage, setUsage] = useState(null);
  const [msg, setMsg] = useState(""); const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // {user, ai} while streaming
  const [result, setResult] = useState(null); const [evaluating, setEvaluating] = useState(false);
  const [speakUsage, setSpeakUsage] = useState(null); const [transcribing, setTranscribing] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("rle_voice") !== "off");
  const [pron, setPron] = useState({}); // keyed by spoken text → {loading, report}
  const recorder = useRecorder(); const speaker = useSpeaker(scenarioId);
  const endRef = useRef(null);
  useEffect(() => { api.get(`/scenarios/${scenarioId}`).then(({data}) => setSc(data.scenario)); }, [scenarioId]);
  useEffect(() => { api.get(`/missions/conversation/${scenarioId}`).then(({data}) => setConvo(data.conversation)); }, [scenarioId]);
  useEffect(() => { api.get("/missions/usage").then(({data}) => setUsage(data.usage)); api.get("/speech/usage").then(({data}) => setSpeakUsage(data.usage)); }, [scenarioId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [convo, pending]);
  const toggleVoice = () => { const next = !voiceOn; setVoiceOn(next); localStorage.setItem("rle_voice", next ? "on" : "off"); if (!next) speaker.stop(); };

  const send = async (e, overrideText) => {
    e?.preventDefault(); const text = (overrideText ?? msg).trim(); if (!text || busy) return;
    setBusy(true); setMsg(""); setPending({ user: text, ai: "" });
    try {
      await streamMissionReply({
        scenarioId, message: text,
        onDelta: (d) => setPending((p) => ({ ...p, ai: (p?.ai || "") + d })),
        onDone: (ev) => {
          setConvo(ev.conversation); setUsage(ev.usage);
          const last = ev.conversation.messages[ev.conversation.messages.length - 1];
          if (voiceOn && last?.role === "ai") speaker.speak(last.text);
        },
      });
    } catch (err) { toast.error(err.message); setMsg(text); }
    finally { setPending(null); setBusy(false); }
  };
  const checkPronunciation = (text, segments) => {
    setPron((p) => ({ ...p, [text]: { loading: true } }));
    api.post("/speech/pronunciation", { transcript: text, segments, scenario_id: scenarioId })
      .then(({ data }) => setPron((p) => ({ ...p, [text]: { report: data.report } })))
      .catch(() => setPron((p) => { const n = { ...p }; delete n[text]; return n; }));
  };
  const onMic = async () => {
    if (recorder.recording) {
      const rec = await recorder.stop(); if (!rec) return;
      setTranscribing(true);
      try {
        const data = await transcribeBlob({ ...rec, scenarioId });
        setSpeakUsage(data.usage);
        if (!data.text) { toast.error("I couldn't hear any words. Try again a little closer to the mic."); return; }
        checkPronunciation(data.text, data.segments || []);
        await send(null, data.text);
      } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Transcription failed"); }
      finally { setTranscribing(false); }
      return;
    }
    if (speakUsage && speakUsage.limit_seconds === 0) { toast.error("Speaking practice is available on Essential and Complete plans."); return; }
    if (speakUsage && speakUsage.remaining_seconds <= 0) { toast.error("You've used your speaking minutes for this month."); return; }
    speaker.stop();
    try { await recorder.start(); } catch { toast.error("Microphone access was blocked. Allow the mic in your browser to speak your replies."); }
  };
  const complete = async () => {
    setEvaluating(true);
    try { const { data } = await api.post("/missions/complete", { scenario_id: scenarioId }); setResult(data.result); toast.success("Mission completed"); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setEvaluating(false); }
  };
  const restart = async () => {
    await api.post("/missions/restart", { scenario_id: scenarioId });
    setConvo(null); setResult(null); toast.success("Mission restarted");
  };
  if (!sc) return <div className="text-slate-500">Loading…</div>;
  if (sc.locked) return (
    <div className="max-w-2xl rle-card p-8 text-center">
      <Lock size={28} className="mx-auto text-amber-600"/>
      <h1 className="mt-3 font-display font-bold text-2xl text-slate-900">Premium mission</h1>
      <p className="text-slate-600 mt-2">Upgrade to unlock this scenario.</p>
      <Link to="/pricing"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 mt-4" data-testid="mission-upgrade-btn">See Pricing</Button></Link>
    </div>
  );

  const messages = [...(convo?.messages || [{ role: "ai", text: sc.opening_message }])];
  if (pending) { messages.push({ role: "user", text: pending.user }); messages.push({ role: "ai", text: pending.ai, streaming: true }); }
  const outOfTurns = usage && usage.remaining === 0;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <span className="rle-eyebrow">{sc.goal} · {sc.difficulty}</span>
            <h1 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl text-slate-900">{sc.title}</h1>
            <p className="text-slate-600 mt-2">{sc.context}</p>
          </div>
          {usage && <UsageMeter usage={usage}/>}
        </div>

        <div className="mt-6 rle-card p-4 sm:p-6">
          <div className="text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2 mb-3">
            <span className="flex items-center gap-1"><Info size={12}/>You are the <b className="ml-1">{sc.learner_role}</b>. The AI plays the <b className="ml-1">{sc.ai_role}</b>.</span>
            <button type="button" onClick={toggleVoice} className={`flex items-center gap-1 px-2.5 py-1 rounded-full border transition-colors ${voiceOn ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-500"}`} data-testid="mission-voice-toggle">
              {voiceOn ? <Volume2 size={12}/> : <VolumeX size={12}/>}{voiceOn ? "Voice replies on" : "Voice replies off"}
            </button>
          </div>
          <div className="max-h-[440px] overflow-y-auto space-y-3 pr-1" data-testid="mission-chat-log">
            {messages.map((m, i) => (
              <div key={i} className={`p-3 rounded-2xl border max-w-[85%] ${m.role === "ai" ? "bg-slate-50 border-slate-200" : "bg-emerald-50 border-emerald-200 ml-auto"}`} data-testid={`mission-message-${i}`}>
                <div className={`text-xs mb-1 flex items-center justify-between gap-2 ${m.role === "ai" ? "text-slate-500" : "text-emerald-700"}`}>
                  <span>{m.role === "ai" ? sc.ai_role : "You"}</span>
                  {m.role === "ai" && !m.streaming && m.text && (
                    <button type="button" onClick={() => speaker.speakingText === m.text ? speaker.stop() : speaker.speak(m.text)} className={`p-1 rounded-full hover:bg-slate-200 ${speaker.speakingText === m.text ? "text-emerald-700 animate-pulse" : "text-slate-400"}`} title="Listen" data-testid={`mission-listen-${i}`}>
                      <Volume2 size={13}/>
                    </button>
                  )}
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">
                  {m.text}{m.streaming && <span className="inline-block w-2 h-4 ml-0.5 align-middle bg-emerald-600 animate-pulse rounded-sm" data-testid="mission-typing-indicator"/>}
                </div>
                {m.role === "user" && pron[m.text] && <PronunciationPanel state={pron[m.text]} speaker={speaker} index={i}/>}
              </div>
            ))}
            <div ref={endRef}/>
          </div>
          {!result && (
            <div className="mt-4 mb-2 flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900" data-testid="mission-speak-hint">
              <Mic size={14} className="mt-0.5 shrink-0"/>
              <span><b>Speaking practice:</b> tap the green <b>Speak</b> button, say your reply out loud, then tap the square to send. The {sc.ai_role.toLowerCase()} replies in text and voice, and you get a pronunciation check under your message.</span>
            </div>
          )}
          {!result && (
            <form onSubmit={send} className="mt-2 flex items-center gap-2">
              <button type="button" onClick={onMic} disabled={busy || transcribing || outOfTurns}
                title={recorder.recording ? "Stop and send" : "Speak your reply"}
                className={`h-11 shrink-0 rounded-full flex items-center justify-center gap-1.5 px-3 sm:px-4 border font-semibold text-sm transition-colors ${recorder.recording ? "bg-red-600 border-red-600 text-white animate-pulse" : transcribing ? "bg-slate-100 border-slate-200 text-slate-400" : "bg-emerald-700 border-emerald-700 text-white hover:bg-emerald-800"}`}
                data-testid="mission-mic-btn">
                {transcribing ? <Loader2 size={18} className="animate-spin"/> : recorder.recording ? <Square size={16}/> : <Mic size={18}/>}
                <span className="hidden sm:inline">{transcribing ? "Transcribing" : recorder.recording ? "Stop & send" : "Speak"}</span>
              </button>
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} disabled={busy || outOfTurns || recorder.recording}
                placeholder={outOfTurns ? "You've used all your AI turns this month" : recorder.recording ? "Listening… tap the square to send" : transcribing ? "Transcribing your voice…" : "Type or tap the mic to speak…"} className="rounded-full" data-testid="mission-reply-input"/>
              <Button type="submit" disabled={busy || outOfTurns || !msg.trim()} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="mission-send-btn">
                {busy ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
              </Button>
            </form>
          )}
          {outOfTurns && !result && (
            <div className="mt-2 text-xs text-amber-700" data-testid="mission-out-of-turns">Monthly AI turn limit reached on the {usage.plan} plan. <Link to="/pricing" className="underline">Upgrade</Link> to keep practising.</div>
          )}
          <div className="mt-2 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-1"><Sparkles size={12}/>Live AI roleplay powered by Claude. Replies are generated and may occasionally be imperfect.</span>
            {speakUsage && (speakUsage.unlimited
              ? <span data-testid="speaking-usage-text">Speaking: {fmtMin(speakUsage.used_seconds)} used · Unlimited (admin)</span>
              : speakUsage.limit_seconds > 0
              ? <span data-testid="speaking-usage-text">Speaking: {fmtMin(speakUsage.used_seconds)} / {speakUsage.limit_seconds / 60} min</span>
              : <Link to="/pricing" className="underline" data-testid="speaking-upgrade-link">Speaking practice: upgrade to unlock</Link>)}
          </div>
        </div>

        {!result ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={complete} disabled={evaluating} variant="outline" className="rounded-full" data-testid="mission-complete-btn">
              {evaluating ? <Loader2 size={16} className="mr-1 animate-spin"/> : <CheckCircle2 size={16} className="mr-1"/>}{evaluating ? "Your coach is reviewing…" : "End & get feedback"}
            </Button>
            {convo && <Button onClick={restart} variant="ghost" className="rounded-full text-slate-600" data-testid="mission-restart-btn"><RotateCcw size={16} className="mr-1"/>Restart</Button>}
          </div>
        ) : (
          <FeedbackReport result={result} onBack={() => nav("/missions")} onRetry={restart}/>
        )}
      </div>

      <aside className="rle-card p-5 h-fit sticky top-24">
        <div className="rle-eyebrow">Mission brief</div>
        <div className="mt-2 text-sm text-slate-800"><b>Objective:</b> {sc.objective}</div>
        <div className="mt-3"><div className="text-xs uppercase text-slate-500 tracking-widest mb-1">Success criteria</div>
          <ul className="text-sm text-slate-700 space-y-1">{sc.success_criteria.map((c,i) => <li key={i}>• {c}</li>)}</ul></div>
        <div className="mt-3"><div className="text-xs uppercase text-slate-500 tracking-widest mb-1">Target vocabulary</div>
          <div className="flex flex-wrap gap-1">{sc.target_vocabulary.map((v) => <span key={v} className="rle-chip">{v}</span>)}</div></div>
        <div className="mt-3"><div className="text-xs uppercase text-slate-500 tracking-widest mb-1">Recommended phrases</div>
          <ul className="text-sm text-slate-700 space-y-1">{sc.recommended_phrases.map((p,i) => <li key={i}>“{p}”</li>)}</ul></div>
      </aside>
    </div>
  );
}

const RUBRIC_LABELS = { task_completion: "Task completion", grammar: "Grammar", vocabulary: "Vocabulary", naturalness: "Naturalness" };

const CLARITY = { clear: ["Clear", "text-emerald-700 bg-emerald-100"], mostly_clear: ["Mostly clear", "text-amber-700 bg-amber-100"], unclear: ["Hard to follow", "text-red-700 bg-red-100"] };

function PronunciationPanel({ state, speaker, index }) {
  if (state.loading) return <div className="mt-2 pt-2 border-t border-emerald-200 text-xs text-emerald-800 flex items-center gap-1" data-testid={`pron-loading-${index}`}><Loader2 size={12} className="animate-spin"/>Checking pronunciation…</div>;
  const r = state.report; if (!r) return null;
  const [label, cls] = CLARITY[r.clarity] || CLARITY.mostly_clear;
  return (
    <div className="mt-2 pt-2 border-t border-emerald-200" data-testid={`pron-panel-${index}`}>
      <div className="flex items-center gap-2 text-xs">
        <span className="flex items-center gap-1 text-emerald-900 font-semibold"><Ear size={12}/>Pronunciation</span>
        <span className={`px-2 py-0.5 rounded-full font-medium ${cls}`} data-testid={`pron-clarity-${index}`}>{label}</span>
      </div>
      {r.note && <p className="text-xs text-slate-600 mt-1">{r.note}</p>}
      {r.flags.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {r.flags.map((f, j) => (
            <li key={j} className="text-xs bg-white/70 border border-emerald-200 rounded-xl px-2.5 py-2" data-testid={`pron-flag-${index}-${j}`}>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => speaker.speak(f.practice || f.word)} className="w-7 h-7 shrink-0 rounded-full bg-emerald-700 text-white flex items-center justify-center hover:bg-emerald-800" title="Hear it" data-testid={`pron-hear-${index}-${j}`}><Volume2 size={13}/></button>
                <span className="font-semibold text-slate-900">{f.word}</span>
                {f.heard_as && <span className="text-slate-500">heard as “{f.heard_as}”</span>}
              </div>
              {f.tip && <div className="mt-1 text-slate-700"><span className="text-slate-500">Say it like:</span> {f.tip}</div>}
              {f.practice && <div className="mt-0.5 text-slate-500 italic">“{f.practice}”</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RubricRow({ k, v }) {
  return (
    <div className="py-3 border-b border-slate-100 last:border-0" data-testid={`rubric-${k}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-slate-900">{RUBRIC_LABELS[k]}</div>
        <div className="flex items-center gap-1" aria-label={`${v.score} out of 5`}>
          {[1,2,3,4,5].map((n) => <span key={n} className={`w-5 h-1.5 rounded-full ${n <= v.score ? "bg-emerald-600" : "bg-slate-200"}`}/>)}
          <span className="ml-2 text-xs font-semibold text-slate-700" data-testid={`rubric-${k}-score`}>{v.score}/5</span>
        </div>
      </div>
      {v.comment && <p className="text-sm text-slate-600 mt-1">{v.comment}</p>}
    </div>
  );
}

function FeedbackReport({ result, onBack, onRetry }) {
  const detailed = result.feedback_type === "detailed";
  return (
    <div className="mt-6 rle-card p-6" data-testid="mission-result">
      <div className="flex items-center justify-between gap-3">
        <span className="rle-eyebrow">{detailed ? "Detailed AI report" : "Feedback"}</span>
        {detailed && <span className="rle-chip flex items-center gap-1"><Sparkles size={12}/>Coach report</span>}
      </div>
      <h3 className="mt-1 font-display font-bold text-xl text-slate-900" data-testid="mission-result-rating">Rating: {result.rating}</h3>
      {result.summary && <p className="text-slate-700 mt-2" data-testid="mission-result-summary">{result.summary}</p>}
      {detailed && result.rubric && (
        <div className="mt-4 rounded-2xl border border-slate-200 px-4" data-testid="mission-rubric">
          {Object.entries(result.rubric).map(([k, v]) => <RubricRow key={k} k={k} v={v}/>)}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div><div className="font-semibold text-slate-900 mb-1">What you did well</div><ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">{result.did_well.map((x,i) => <li key={i}>{x}</li>)}</ul></div>
        <div><div className="font-semibold text-slate-900 mb-1">Try this</div><ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">{result.try_this.map((x,i) => <li key={i}>{x}</li>)}</ul></div>
      </div>
      {detailed && result.mistakes_added > 0 && (
        <div className="mt-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="mission-mistakes-added">
          {result.mistakes_added} correction{result.mistakes_added === 1 ? "" : "s"} added to your <Link to="/mistakes" className="underline font-semibold">Mistake Bank</Link>.
        </div>
      )}
      <p className="text-xs text-slate-500 mt-4" data-testid="mission-result-notice">{result.notice}{!detailed && <> <Link to="/pricing" className="underline text-emerald-700">See plans</Link></>}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={onBack} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="mission-back-list-btn">Back to missions</Button>
        <Button onClick={onRetry} variant="outline" className="rounded-full" data-testid="mission-retry-btn"><RotateCcw size={16} className="mr-1"/>Try again</Button>
      </div>
    </div>
  );
}
